import React, { useCallback, useMemo, useState } from 'react';
import './cycle/cycle.css';

import LivingOrb                 from './cycle/LivingOrb';
import BottomGlassSheet          from './cycle/BottomGlassSheet';
import TodayBodyCard             from './cycle/TodayBodyCard';
import CurrentCycleCard          from './cycle/CurrentCycleCard';
import TodaysIntelligenceScreen  from './cycle/TodaysIntelligenceScreen';
import PlaceholderScreen         from './cycle/PlaceholderScreen';
import SymptomsScreen            from './SymptomsScreen';
import HormoneTrendsScreen       from './cycle/HormoneTrendsScreen';
import BodySnapshotScreen        from './cycle/BodySnapshotScreen';
import SmartSuggestionsScreen    from './cycle/SmartSuggestionsScreen';
import CycleTimelineScreen       from './cycle/CycleTimelineScreen';
import ReportsScreen             from './cycle/ReportsScreen';
import PeriodLogScreen           from './cycle/PeriodLogScreen';
import RecentCyclesScreen        from './cycle/RecentCyclesScreen';
import CycleSettingsScreen       from './cycle/CycleSettingsScreen';
import InsightsScreen            from './InsightsScreen';
import AcknowledgmentPulseHost   from './cycle/AcknowledgmentPulse';
import useCycleData              from '../hooks/useCycleData';
import { RECENT_CYCLES }         from './cycle/phaseData';

/* ─── Sheet navigation model ─── */
const SHEET_ROUTES = [
  { id: 'todays-intelligence', title: "Today's Intelligence",  sub: 'What your body is doing right now', badge: 'Live',   badgeTone: 'live',   fullyBuilt: true  },
  { id: 'body-snapshot',       title: 'Body Snapshot',          sub: 'Energy · Symptoms · Recovery',      badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'symptoms',            title: 'Symptoms',                sub: 'Log & browse your history',        badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'hormone-trends',      title: 'Hormone Trends',          sub: 'Estrogen · Progesterone · LH',     badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'pattern-insights',    title: 'Pattern Insights',        sub: 'What Theo has learned',            badge: '3 New',  badgeTone: 'new',    fullyBuilt: false },
  { id: 'smart-suggestions',   title: 'Smart Suggestions',       sub: 'Three small things for today',     badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'cycle-timeline',      title: 'Cycle Timeline',          sub: 'Bleed · Rise · Peak · Luteal',     badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'recent-cycles',       title: 'Recent Cycles',           sub: 'Your full cycle history',          badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'reports',             title: 'Reports',                 sub: 'Monthly & shareable summaries',    badge: null,     badgeTone: null,     fullyBuilt: false },
  { id: 'settings',            title: 'Settings',                sub: 'Notifications · Data · Privacy',   badge: null,     badgeTone: null,     fullyBuilt: false },
];

const SECTIONS = [
  { label: 'Today',         ids: ['todays-intelligence', 'body-snapshot', 'symptoms', 'smart-suggestions'] },
  { label: 'Your Story',    ids: ['cycle-timeline', 'recent-cycles'] },
  { label: 'Understanding', ids: ['pattern-insights', 'hormone-trends', 'reports'] },
  { label: 'App',           ids: ['settings'] },
];

/**
 * CycleSubScreen — thin chrome for wired existing production screens
 * (Symptoms / Hormone Trends / Pattern Insights). Renders the same
 * sticky `.cx-sub-hd` back-bar used by Today's Intelligence so wired
 * destinations feel native to the Cycle flow, without touching the
 * embedded screen's internal layout.
 */
function CycleSubScreen({ phase, eyebrow, title, onBack, children }) {
  return (
    <div className="cycle-experience" data-phase={phase} data-testid="cycle-experience">
      <div className="cx-subscreen">
        <header className="cx-sub-hd">
          <button className="cx-back-btn" onClick={onBack} aria-label="Back" data-testid="subscreen-back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="m15 6-6 6 6 6"/>
            </svg>
          </button>
          <div className="cx-sub-title">
            <span className="cx-sub-eyebrow">{eyebrow}</span>
            {title}
          </div>
        </header>
        <div className="cx-sub-body cx-sub-body--embed">{children}</div>
      </div>
      <AcknowledgmentPulseHost/>
    </div>
  );
}

/**
 * CycleScreen — Phase 1 production build.
 *
 *   • LivingOrb (5-layer biological hero)
 *   • PhaseNavigation (sliding-pill dev switcher; will be gated to
 *     production users later)
 *   • Vertical stack: Today's Body Card + Current Cycle Card
 *   • BottomGlassSheet (reusable, phase-tinted, iOS spring physics)
 *   • Sub-screen router — Today's Intelligence is fully built;
 *     the other 9 destinations render as PlaceholderScreen
 *
 * State is intentionally local for Phase 1. When Supabase-backed cycle
 * data lands, `phase`, `dayNumber`, `waiting` will come from a
 * useCycleData(userId) hook and everything else stays the same.
 */
export default function CycleScreen({ userId }) {
  // Live cycle state — reads from Supabase user_cycle_data, derives
  // phase/day/waiting, returns a view-shape compatible with the demo
  // PHASES seed so cards + orb + intelligence keep rendering.
  const { phase, dayNumber, cycleLength, waiting, data, expectedNext, refetch } = useCycleData(userId);
  const [subScreen,  setSubScreen]  = useState(null);
  // Persistent Explore Sheet — remembers expansion + scroll across
  // sub-screen visits so the user never re-navigates from scratch.
  const [sheetOpenBeforeNav, setSheetOpenBeforeNav] = useState(false);
  const sheetScrollRef = React.useRef(0);

  // Live-updating date marker — computed on first render only.
  const dateLabel = useMemo(() => {
    const d = new Date();
    const opts = { weekday: 'long', day: 'numeric', month: 'long' };
    return d.toLocaleDateString('en-GB', opts).replace(',', ' ·');
  }, []);

  const openRoute = useCallback((id) => {
    // Remember that the sheet was open when we navigated in, plus
    // the current scroll position, so Back restores exactly.
    const sheetEl = document.querySelector('.cx-sheet.cx-expanded');
    if (sheetEl) {
      setSheetOpenBeforeNav(true);
      const body = sheetEl.querySelector('.cx-sheet-body');
      if (body) sheetScrollRef.current = body.scrollTop;
    } else {
      setSheetOpenBeforeNav(false);
    }
    setSubScreen(id);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const closeSubScreen = useCallback(() => {
    setSubScreen(null);
    // Restore the sheet + its scroll after mount tick
    requestAnimationFrame(() => {
      if (sheetOpenBeforeNav) {
        const sheetEl = document.querySelector('.cx-sheet');
        sheetEl?.classList.add('cx-expanded');
        const scrim = document.querySelector('.cx-sheet-scrim');
        scrim?.classList.add('cx-visible');
        const body = sheetEl?.querySelector('.cx-sheet-body');
        if (body) body.scrollTop = sheetScrollRef.current;
      }
    });
  }, [sheetOpenBeforeNav]);

  // ─── Sub-screen router ───
  if (subScreen === 'todays-intelligence') {
    return (
      <div className="cycle-experience" data-phase={phase} data-testid="cycle-experience">
        <TodaysIntelligenceScreen phase={phase} onBack={closeSubScreen}/>
      </div>
    );
  }
  if (subScreen === 'symptoms') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Today · Symptoms" title="Log & browse" onBack={closeSubScreen}>
        <SymptomsScreen userId={userId}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'body-snapshot') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Today · Body Snapshot" title="How you are today" onBack={closeSubScreen}>
        <BodySnapshotScreen userId={userId} phase={phase} dayNumber={dayNumber} waiting={waiting} cycleLength={cycleLength}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'hormone-trends') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Trends · Hormones" title={`Expected · ${phase} phase`} onBack={closeSubScreen}>
        <HormoneTrendsScreen userId={userId} phase={phase}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'smart-suggestions') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Theo · Suggestions" title="Personalised for today" onBack={closeSubScreen}>
        <SmartSuggestionsScreen userId={userId} phase={phase} dayNumber={dayNumber} waiting={waiting}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'cycle-timeline') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Story · Cycle" title="Your body's story this cycle" onBack={closeSubScreen}>
        <CycleTimelineScreen userId={userId} dayNumber={dayNumber} cycleLength={cycleLength} expectedNext={expectedNext}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'reports') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Reports · Summary" title="Compiled from your data" onBack={closeSubScreen}>
        <ReportsScreen userId={userId} phase={phase} dayNumber={dayNumber} cycleLength={cycleLength} waiting={waiting}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'period-log') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Cycle · Log Period" title="Start today's period" onBack={closeSubScreen}>
        <PeriodLogScreen
          userId={userId}
          cycleLength={cycleLength}
          onSaved={() => { refetch(); closeSubScreen(); }}
        />
      </CycleSubScreen>
    );
  }
  if (subScreen === 'pattern-insights') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Patterns · Theo" title="What Theo has learned" onBack={closeSubScreen}>
        <InsightsScreen userId={userId}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'recent-cycles') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Memory · Recent Cycles" title="The story of your body" onBack={closeSubScreen}>
        <RecentCyclesScreen userId={userId}/>
      </CycleSubScreen>
    );
  }
  if (subScreen === 'settings') {
    return (
      <CycleSubScreen phase={phase} eyebrow="Settings · Cycle" title="Make it yours" onBack={closeSubScreen}>
        <CycleSettingsScreen userId={userId} cycleLength={cycleLength}/>
      </CycleSubScreen>
    );
  }
  if (subScreen && SHEET_ROUTES.find(r => r.id === subScreen)) {
    return (
      <div className="cycle-experience" data-phase={phase} data-testid="cycle-experience">
        <PlaceholderScreen screenId={subScreen} onBack={closeSubScreen}/>
      </div>
    );
  }

  // ─── Sheet content: recent cycles + grouped nav rows ───
  const sheetContent = (
    <>
      <div className="cx-sheet-section-lbl">Recent Cycles</div>
      {RECENT_CYCLES.map(rc => (
        <div key={rc.month} className="cx-rc-row">
          <div className="cx-rc-mo">{rc.month}</div>
          <div className="cx-rc-range">
            {rc.range.split('→')[0].trim()} <span className="cx-to">→</span> {rc.range.split('→')[1].trim()}
          </div>
          <div className="cx-rc-length">{rc.length}</div>
        </div>
      ))}

      {SECTIONS.map(section => (
        <React.Fragment key={section.label}>
          <div className="cx-sheet-section-lbl">{section.label}</div>
          {section.ids.map(id => {
            const route = SHEET_ROUTES.find(r => r.id === id);
            if (!route) return null;
            return (
              <button
                key={id}
                className="cx-nav-row"
                onClick={() => openRoute(id)}
                data-testid={`sheet-nav-${id}`}
              >
                <div>
                  <div className="cx-r-t">{route.title}</div>
                  <div className="cx-r-s">{route.sub}</div>
                </div>
                <div className="cx-r-r">
                  {route.badge && <span className={`cx-r-badge${route.badgeTone ? ' cx-r-badge--' + route.badgeTone : ''}`}>{route.badge}</span>}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <path d="m9 6 6 6-6 6"/>
                  </svg>
                </div>
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );

  return (
    <div className="cycle-experience" data-phase={phase} data-testid="cycle-experience">
      {/* Background */}
      <div className="cx-bg-atmosphere" aria-hidden="true"/>
      <div className="cx-bg-blob a"     aria-hidden="true"/>
      <div className="cx-bg-blob b"     aria-hidden="true"/>
      <div className="cx-bg-blob c"     aria-hidden="true"/>
      <div className="cx-bg-wave"       aria-hidden="true"/>
      <div className="cx-bg-noise"      aria-hidden="true"/>
      <div className="cx-bg-particles" aria-hidden="true">
        {Array.from({ length: 20 }, (_, i) => (
          <span key={i} className="cx-bg-p" style={{
            left: (Math.random() * 100) + '%',
            top:  (30 + Math.random() * 50) + '%',
            '--bd':  (22 + Math.random() * 14).toFixed(2) + 's',
            '--bdl': (-Math.random() * 30).toFixed(2) + 's',
            '--bx':  (Math.random() * 20 - 10).toFixed(1) + 'px',
            '--by':  (-30 - Math.random() * 40).toFixed(1) + 'px',
          }}/>
        ))}
      </div>

      {/* Phase preview switcher removed in Phase A — phase now derives
          from real cycle data via useCycleData(userId) in Phase 2. */}

      <div className="cx-content">
        {/* Meta row */}
        <div className="cx-meta">
          <div className="cx-date">{dateLabel}</div>
          <div className="cx-streak">Cycle 6</div>
        </div>

        {/* Living Orb — the hero */}
        <LivingOrb
          tag={data.tag}
          day={data.day}
          of={data.of}
          phaseName={data.name}
          sentence={data.sentence}
        />

        {/* Vertical porcelain cards */}
        <div className="cx-cards">
          <TodayBodyCard
            energy={data.body.energy}
            mood={data.body.mood}
            recovery={data.body.recovery}
            focus={data.body.focus}
            onDetails={() => openRoute('body-snapshot')}
          />
          <CurrentCycleCard
            day={data.card.day}
            meta={data.card.meta}
            confidence={data.card.confidence}
            confidenceOn={data.card.confidenceOn}
            waiting={data.waiting}
            waitingText={data.card.waitingStrip}
            cta={data.cta}
            onHistory={() => openRoute('recent-cycles')}
            onLog={() => openRoute('period-log')}
          />
        </div>
      </div>

      {/* Liquid glass bottom sheet */}
      <BottomGlassSheet
        header={{ title: 'Explore your cycle', sub: 'Intelligence · timeline · reports' }}
        collapsedOffset={42}
        expandedTopVh={12}
      >
        {sheetContent}
      </BottomGlassSheet>
      {/* Living Companion acknowledgment layer — pulses on save actions */}
      <AcknowledgmentPulseHost/>
    </div>
  );
}
